// client/src/coms/components/SocialSidebar.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { comsApiGet, comsApiPost } from '../comsApi';
import SidebarTopDrawer from './sidebar_parts/SidebarTopDrawer';
import { SidebarFooter } from './sidebar_parts/SidebarFooter';
import {
  avatarUrlForUserId,
  getServerLogoUrl,
  makeAcronym,
  pickServerKey,
  safeArray,
  safeObj,
  toInt,
} from './sidebar_parts/sidebarUtils';


function isGenericUserLabel(value, userId) {
  const text = String(value || '').trim();
  if (!text) return true;
  const uid = toInt(userId, 0);
  const compact = text.toLowerCase().replace(/\s+/g, ' ');
  if (compact === 'user' || compact === 'unknown user') return true;
  if (/^user\s*#?\??$/.test(compact)) return true;
  if (uid > 0 && compact === `user ${uid}`) return true;
  if (uid > 0 && compact === `user #${uid}`) return true;
  return false;
}

function pickUserLabel(userObj, userId, serverMemberNameMap) {
  const userData = safeObj(userObj);
  const uid = toInt(userId != null ? userId : userData.id != null ? userData.id : userData.user_id, 0);
  const serverName = uid > 0 ? String(safeObj(serverMemberNameMap)[String(uid)] || '').trim() : '';
  const candidates = [
    userData.display_name,
    userData.displayName,
    userData.name,
    userData.label,
    userData.username,
    userData.email,
    serverName,
  ];

  for (const candidate of candidates) {
    const label = String(candidate || '').trim();
    if (label && !isGenericUserLabel(label, uid)) return label;
  }

  if (serverName) return serverName;
  return `User ${uid || '?'}`;
}

function buildServerMemberNameMap(server) {
  const out = {};
  const members = safeArray(server && server.members);

  for (const raw of members) {
    const m = safeObj(raw);
    const uid = toInt(m.id != null ? m.id : m.user_id != null ? m.user_id : m.userId, 0);
    if (!uid) continue;

    const displayName = String(m.display_name || '').trim();
    const username = String(m.username || '').trim();
    const name = String(m.name || '').trim();
    const best = displayName || username || name;

    if (best) out[String(uid)] = best;
  }

  return out;
}

function normalizeFriendItem(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel({ ...other, username: o.username || other.username, label: o.label || other.label }, otherUserId, serverMemberNameMap);

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
  };
}

function normalizeRequestItem(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel(other, otherUserId, serverMemberNameMap);
  const serverName = otherUserId > 0 ? String(safeObj(serverMemberNameMap)[String(otherUserId)] || '').trim() : '';

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
    label: serverName || username,
    status: String(o.status || '').trim(),
    requested_at: o.requested_at ? String(o.requested_at) : '',
  };
}

function normalizeThreadItem(item, serverMemberNameMap) {
  const o = safeObj(item);
  const other = safeObj(o.other_user);
  const otherUserId = toInt(o.other_user_id != null ? o.other_user_id : other.id, 0);
  const username = pickUserLabel({ ...other, username: o.username || other.username, label: o.label || other.label }, otherUserId, serverMemberNameMap);

  return {
    id: toInt(o.id, 0),
    other_user_id: otherUserId,
    username,
    label: username,
    can_send: !!o.can_send,
    is_blocked: !!o.is_blocked,
    last_message_at: o.last_message_at ? String(o.last_message_at) : '',
    has_unread: !!o.has_unread,
    latest_message_id: toInt(o.latest_message_id, 0),
    viewer_last_read_message_id: toInt(o.viewer_last_read_message_id, 0),
    other_user_last_read_message_id: toInt(o.other_user_last_read_message_id, 0),
  };
}

function normalizeBlockItem(item, serverMemberNameMap) {
  const o = safeObj(item);
  const blocked = safeObj(o.blocked_user);
  const blockedUserId = toInt(o.blocked_user_id != null ? o.blocked_user_id : blocked.id, 0);
  const username = pickUserLabel({ ...blocked, username: o.username || blocked.username, label: o.label || blocked.label }, blockedUserId, serverMemberNameMap);

  return {
    blocked_user_id: blockedUserId,
    username,
    created_at: o.created_at ? String(o.created_at) : '',
  };
}

function sectionTitleStyle() {
  return {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.48 * var(--vx-ov, 1)))',
    textTransform: 'uppercase',
    padding: '12px 14px 8px',
  };
}

function rowStyle(active) {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    border: 'none',
    background: active ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'transparent',
    color: 'var(--vx-text, #fff)',
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: 10,
  };
}

function itemMetaStyle() {
  return {
    fontSize: 11,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))',
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}

function tabsWrapStyle() {
  return {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 8,
    padding: '10px 12px 6px',
    flexShrink: 0,
  };
}

function tabButtonStyle(active) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 36,
    padding: '0 12px',
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
    background: active ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
    color: 'var(--vx-text, #fff)',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    position: 'relative',
  };
}

function tabBadgeStyle(kind = 'dot') {
  if (kind === 'count') {
    return {
      minWidth: 18,
      height: 18,
      padding: '0 6px',
      borderRadius: 999,
      background: '#ff5d73',
      color: '#fff',
      fontSize: 11,
      fontWeight: 900,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
    };
  }

  return {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#ff5d73',
    flex: '0 0 auto',
    boxShadow: '0 0 0 2px rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
  };
}

function unreadDotStyle() {
  return {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#ff5d73',
    flex: '0 0 auto',
    marginLeft: 8,
  };
}

function contextMenuStyle(x, y) {
  return {
    position: 'fixed',
    left: x,
    top: y,
    minWidth: 180,
    padding: 6,
    borderRadius: 12,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
    background: 'rgba(var(--vxs-2-rgb, 20,22,27), 0.98)',
    boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(10px)',
    zIndex: 10000,
  };
}

function contextMenuButtonStyle(danger = false) {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 36,
    padding: '0 12px',
    border: 'none',
    borderRadius: 9,
    background: 'transparent',
    color: danger ? '#ff8f9d' : 'var(--vx-text, #fff)',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'left',
  };
}

function SocialListRow({ label, meta, active, onClick, onContextMenu, avatarUrl, unread = false }) {
  return (
    <button type="button" style={rowStyle(active)} onClick={onClick} onContextMenu={onContextMenu}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          overflow: 'hidden',
          flex: '0 0 auto',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            aria-hidden="true"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{ fontSize: 12, fontWeight: 800, color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.85 * var(--vx-ov, 1)))' }}>
            {String(label || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          {unread ? <span style={unreadDotStyle()} /> : null}
        </div>
        {meta ? <div style={itemMetaStyle()}>{meta}</div> : null}
      </div>
    </button>
  );
}

export default function SocialSidebar({
  server,
  servers,
  user,
  onSelectServer,
  onCreateServer,
  onOpenTab,
  socialSection,
  onSelectSocialSection,
  selectedSocialThreadId,
  onSelectSocialThreadId,
  serverUnreadById,
  serverMentionCountById,
  hideServerDrawer = false,
  onOpenServerSettings,
  onServersChanged,
}) {
  const navigate = useNavigate();
  const serverKey = useMemo(() => pickServerKey(server), [server]);
  const serverMemberNameMap = useMemo(() => buildServerMemberNameMap(server), [server]);
  const [query, setQuery] = useState('');
  const [logoBroken, setLogoBroken] = useState(false);
  const [fetchedLogoUrl, setFetchedLogoUrl] = useState('');
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [threads, setThreads] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [userMenu, setUserMenu] = useState(null);
  const [mutatingUserId, setMutatingUserId] = useState(0);

  useEffect(() => {
    setLogoBroken(false);
    setFetchedLogoUrl('');
  }, [
    serverKey,
    server && server.logo_url,
    server && server.logoUrl,
    server && server.server_logo_url,
    server && server.serverLogoUrl,
  ]);

  useEffect(() => {
    let alive = true;

    async function loadLogoFallback() {
      const sid = serverKey ? String(serverKey).trim() : '';
      if (!sid) return;

      const direct = getServerLogoUrl(server);
      if (direct) return;

      try {
        const r = await comsApiGet(`/server-logo/${encodeURIComponent(sid)}`);
        if (!alive) return;
        const logo = r && r.logo_url ? String(r.logo_url).trim() : '';
        setFetchedLogoUrl(logo);
      } catch {
        if (!alive) return;
        setFetchedLogoUrl('');
      }
    }

    loadLogoFallback().catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverKey, server]);

  async function loadSocialState() {
    const [friendsRes, reqRes, dmsRes, blocksRes] = await Promise.all([
      comsApiGet('/friends').catch(() => null),
      comsApiGet('/friend-requests').catch(() => null),
      comsApiGet('/dms').catch(() => null),
      comsApiGet('/blocks').catch(() => null),
    ]);

    setFriends(safeArray(friendsRes && friendsRes.friends).map((item) => normalizeFriendItem(item, serverMemberNameMap)));
    setIncoming(safeArray(reqRes && reqRes.incoming).map((item) => normalizeRequestItem(item, serverMemberNameMap)));
    setOutgoing(safeArray(reqRes && reqRes.outgoing).map((item) => normalizeRequestItem(item, serverMemberNameMap)));
    setThreads(safeArray(dmsRes && dmsRes.threads).map((item) => normalizeThreadItem(item, serverMemberNameMap)));
    setBlocks(safeArray(blocksRes && blocksRes.blocks).map((item) => normalizeBlockItem(item, serverMemberNameMap)));
  }

  useEffect(() => {
    let alive = true;
    let timer = null;

    async function loadSocial() {
      try {
        const [friendsRes, reqRes, dmsRes, blocksRes] = await Promise.all([
          comsApiGet('/friends').catch(() => null),
          comsApiGet('/friend-requests').catch(() => null),
          comsApiGet('/dms').catch(() => null),
          comsApiGet('/blocks').catch(() => null),
        ]);

        if (!alive) return;

        setFriends(safeArray(friendsRes && friendsRes.friends).map((item) => normalizeFriendItem(item, serverMemberNameMap)));
        setIncoming(safeArray(reqRes && reqRes.incoming).map((item) => normalizeRequestItem(item, serverMemberNameMap)));
        setOutgoing(safeArray(reqRes && reqRes.outgoing).map((item) => normalizeRequestItem(item, serverMemberNameMap)));
        setThreads(safeArray(dmsRes && dmsRes.threads).map((item) => normalizeThreadItem(item, serverMemberNameMap)));
        setBlocks(safeArray(blocksRes && blocksRes.blocks).map((item) => normalizeBlockItem(item, serverMemberNameMap)));
      } catch {
        if (!alive) return;
        setFriends([]);
        setIncoming([]);
        setOutgoing([]);
        setThreads([]);
        setBlocks([]);
      }
    }

    loadSocial().catch(() => {});
    timer = setInterval(() => {
      loadSocial().catch(() => {});
    }, 5000);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [serverMemberNameMap]);

  useEffect(() => {
    function closeMenu() {
      setUserMenu(null);
    }

    function onWindowMouseDown() {
      closeMenu();
    }

    function onWindowBlur() {
      closeMenu();
    }

    function onWindowKeyDown(e) {
      if (e && e.key === 'Escape') closeMenu();
    }

    function onWindowScroll() {
      closeMenu();
    }

    window.addEventListener('mousedown', onWindowMouseDown);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('keydown', onWindowKeyDown);
    window.addEventListener('scroll', onWindowScroll, true);

    return () => {
      window.removeEventListener('mousedown', onWindowMouseDown);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('keydown', onWindowKeyDown);
      window.removeEventListener('scroll', onWindowScroll, true);
    };
  }, []);

  const serverLogoUrl = useMemo(() => {
    const direct = getServerLogoUrl(server);
    return String(direct || fetchedLogoUrl || '').trim();
  }, [server, fetchedLogoUrl]);

  const hasServerLogo = !!serverLogoUrl && !logoBroken;
  const serverAcronym = useMemo(() => makeAcronym(server && server.name), [server]);

  const displayName = useMemo(() => {
    if (!user) return 'Guest';
    const v = user.display_name || user.displayName || user.username || user.name || user.email || 'User';
    return String(v || '').trim() || 'User';
  }, [user]);

  const myUserId = useMemo(() => {
    const u = user && typeof user === 'object' ? user : null;
    const id = u && (u.id != null ? u.id : u.user_id != null ? u.user_id : null);
    return toInt(id, 0);
  }, [user]);

  const myAvatarUrl = useMemo(() => avatarUrlForUserId(myUserId), [myUserId]);

  const blockedUserIdSet = useMemo(() => {
    const set = new Set();
    safeArray(blocks).forEach((item) => {
      const uid = toInt(item && item.blocked_user_id, 0);
      if (uid > 0) set.add(uid);
    });
    return set;
  }, [blocks]);

  const filteredFriends = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((x) => String(x.username || '').toLowerCase().includes(q));
  }, [friends, query]);

  const filteredIncoming = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return incoming;
    return incoming.filter((x) => String(x.label || x.username || '').toLowerCase().includes(q));
  }, [incoming, query]);

  const filteredOutgoing = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return outgoing;
    return outgoing.filter((x) => String(x.label || x.username || '').toLowerCase().includes(q));
  }, [outgoing, query]);

  const filteredBlocks = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return blocks;
    return blocks.filter((x) => String(x.username || '').toLowerCase().includes(q));
  }, [blocks, query]);

  const totalIncomingCount = incoming.length;
  const hasAnyPendingIncoming = totalIncomingCount > 0;
  const hasAnyDmUnread = useMemo(
    () => safeArray(threads).some((thread) => !!(thread && thread.has_unread)),
    [threads]
  );
  const blockedCount = blocks.length;

  function handleDrawerSelectServer(sid) {
    const nextId = String(sid || '').trim();
    if (!nextId) return;

    if (typeof onSelectServer === 'function') {
      onSelectServer(nextId);
      return;
    }

    navigate(`/comms/${encodeURIComponent(nextId)}`);
  }

  function openSection(section) {
    if (typeof onSelectSocialSection === 'function') onSelectSocialSection(section);
  }

  async function openFriendChat(friend) {
    const friendUserId = toInt(friend && friend.other_user_id, 0);
    if (!friendUserId) return;
    if (blockedUserIdSet.has(friendUserId)) return;

    try {
      const res = await comsApiPost('/dms/thread', { user_id: friendUserId });
      const threadId = toInt(res && res.thread && res.thread.id, 0);
      if (!threadId) return;

      if (typeof onSelectSocialSection === 'function') onSelectSocialSection('dms');
      if (typeof onSelectSocialThreadId === 'function') onSelectSocialThreadId(String(threadId));
    } catch {}
  }

  function openUserMenu(e, item, source = 'friends') {
    if (!e) return;
    e.preventDefault();
    e.stopPropagation();

    const targetUserId =
      source === 'blocked'
        ? toInt(item && item.blocked_user_id, 0)
        : toInt(item && item.other_user_id, 0);

    if (!targetUserId || targetUserId === myUserId) {
      setUserMenu(null);
      return;
    }

    const label = String(
      source === 'blocked'
        ? item && item.username
        : item && (item.label || item.username)
    ).trim() || `User ${targetUserId}`;

    const isBlocked = blockedUserIdSet.has(targetUserId);

    const menuWidth = 196;
    const menuHeight = 54;
    const maxX = Math.max(8, window.innerWidth - menuWidth - 8);
    const maxY = Math.max(8, window.innerHeight - menuHeight - 8);
    const x = Math.max(8, Math.min(e.clientX, maxX));
    const y = Math.max(8, Math.min(e.clientY, maxY));

    setUserMenu({
      x,
      y,
      user_id: targetUserId,
      label,
      is_blocked: isBlocked,
      source,
    });
  }

  async function deleteBlock(targetUserId) {
    const res = await fetch(`/api/coms/blocks/${targetUserId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    let data = null;
    try {
      data = await res.json();
    } catch {}

    if (!res.ok) {
      const msg =
        (data && (data.error || data.detail || data.message)) ||
        `Failed to unblock user (${res.status})`;
      throw new Error(msg);
    }

    return data;
  }

  async function handleToggleBlockFromMenu() {
    const targetUserId = toInt(userMenu && userMenu.user_id, 0);
    if (!targetUserId || mutatingUserId) return;

    const isBlocked = !!(userMenu && userMenu.is_blocked);

    setMutatingUserId(targetUserId);
    try {
      if (isBlocked) {
        await deleteBlock(targetUserId);
      } else {
        await comsApiPost(`/blocks/${targetUserId}`, {});
      }

      const selectedThread = safeArray(threads).find(
        (thread) => toInt(thread && thread.other_user_id, 0) === targetUserId
      );

      setUserMenu(null);
      await loadSocialState();

      if (selectedThread && String(selectedSocialThreadId || '') === String(selectedThread.id || '')) {
        if (typeof onSelectSocialThreadId === 'function') onSelectSocialThreadId('');
        if (typeof onSelectSocialSection === 'function') onSelectSocialSection('friends');
      }
    } catch (err) {
      try {
        window.alert(String((err && err.message) || err || 'Failed to update block state'));
      } catch {}
    } finally {
      setMutatingUserId(0);
    }
  }

  const friendsTabActive = socialSection === 'friends' || socialSection === 'dms';
  const pendingTabActive = socialSection === 'pending';
  const blockedTabActive = socialSection === 'blocked';

  return (
    <div className="coms-sidebar" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {hideServerDrawer ? null : (
        <SidebarTopDrawer
          servers={servers}
          activeServerId={serverKey}
          onSelectServer={handleDrawerSelectServer}
          onCreateServer={onCreateServer}
          onServersChanged={onServersChanged}
          onOpenServerSettings={onOpenServerSettings}
          user={user}
          serverUnreadById={serverUnreadById}
          serverMentionCountById={serverMentionCountById}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
          flexShrink: 0,
        }}
      >
        <div
          title={server && server.name ? String(server.name) : 'Server'}
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
            flex: '0 0 auto',
          }}
        >
          {hasServerLogo ? (
            <img
              src={serverLogoUrl}
              alt=""
              aria-hidden="true"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: '0.03em',
                color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.95 * var(--vx-ov, 1)))',
              }}
            >
              {serverAcronym}
            </span>
          )}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: 'var(--vx-text, #fff)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Social
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Friends and direct messages
          </div>
        </div>
      </div>

      <div className="coms-sidebar-search" style={{ flexShrink: 0 }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find people..." />
      </div>

      <div style={tabsWrapStyle()}>
        <button
          type="button"
          style={tabButtonStyle(friendsTabActive)}
          onClick={() => openSection('friends')}
        >
          <span>Friends</span>
          {hasAnyDmUnread ? <span style={tabBadgeStyle('dot')} /> : null}
        </button>

        <button
          type="button"
          style={tabButtonStyle(pendingTabActive)}
          onClick={() => openSection('pending')}
        >
          <span>Pending</span>
          {hasAnyPendingIncoming ? (
            <span style={tabBadgeStyle('count')}>
              {totalIncomingCount > 99 ? '99+' : String(totalIncomingCount)}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          style={tabButtonStyle(blockedTabActive)}
          onClick={() => openSection('blocked')}
        >
          <span>Blocked</span>
          {blockedCount > 0 ? (
            <span style={tabBadgeStyle('count')}>
              {blockedCount > 99 ? '99+' : String(blockedCount)}
            </span>
          ) : null}
        </button>
      </div>

      <div className="coms-sidebar-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 8 }}>
        <div style={sectionTitleStyle()}>
          {blockedTabActive ? 'Blocked' : pendingTabActive ? 'Pending' : 'Friends'}
        </div>

        {blockedTabActive ? (
          <div style={{ display: 'grid', gap: 4 }}>
            {filteredBlocks.length ? filteredBlocks.map((block) => (
              <SocialListRow
                key={`block_${block.blocked_user_id}`}
                label={block.username}
                meta="Blocked user"
                active={false}
                onClick={() => openSection('blocked')}
                onContextMenu={(e) => openUserMenu(e, block, 'blocked')}
                avatarUrl={avatarUrlForUserId(block.blocked_user_id)}
              />
            )) : (
              <div style={{ padding: '10px 12px', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))', fontSize: 13 }}>No blocked users</div>
            )}
          </div>
        ) : pendingTabActive ? (
          <div style={{ display: 'grid', gap: 4 }}>
            {filteredIncoming.length ? filteredIncoming.map((req) => (
              <SocialListRow
                key={`in_${req.id}`}
                label={req.label || req.username}
                meta="Incoming request"
                active={false}
                onClick={() => openSection('pending')}
                onContextMenu={(e) => openUserMenu(e, req, 'incoming')}
                avatarUrl={avatarUrlForUserId(req.other_user_id)}
              />
            )) : (
              <div style={{ padding: '10px 12px', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))', fontSize: 13 }}>No incoming requests</div>
            )}

            {filteredOutgoing.length ? filteredOutgoing.map((req) => (
              <SocialListRow
                key={`out_${req.id}`}
                label={req.label || req.username}
                meta="Outgoing request"
                active={false}
                onClick={() => openSection('pending')}
                onContextMenu={(e) => openUserMenu(e, req, 'outgoing')}
                avatarUrl={avatarUrlForUserId(req.other_user_id)}
              />
            )) : null}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {filteredFriends.length ? filteredFriends.map((friend) => {
              const activeThread = safeArray(threads).find(
                (thread) =>
                  toInt(thread && thread.other_user_id, 0) === toInt(friend && friend.other_user_id, 0)
              );

              const isActive =
                socialSection === 'dms' &&
                !!activeThread &&
                String(selectedSocialThreadId || '') === String(activeThread.id || '');

              const hasUnread = !!(activeThread && activeThread.has_unread);
              const isBlocked = blockedUserIdSet.has(toInt(friend && friend.other_user_id, 0));

              return (
                <SocialListRow
                  key={friend.other_user_id}
                  label={friend.username}
                  meta={isBlocked ? 'Blocked user' : 'Open direct message'}
                  active={isActive}
                  unread={hasUnread && !isActive}
                  onClick={() => {
                    if (isBlocked) return;
                    openFriendChat(friend);
                  }}
                  onContextMenu={(e) => openUserMenu(e, friend, 'friends')}
                  avatarUrl={avatarUrlForUserId(friend.other_user_id)}
                />
              );
            }) : (
              <div style={{ padding: '10px 12px', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.55 * var(--vx-ov, 1)))', fontSize: 13 }}>No friends yet</div>
            )}
          </div>
        )}
      </div>

      <div className="coms-sidebar-footer-wrap" style={{ marginTop: 'auto', flexShrink: 0 }}>
        <SidebarFooter
          showVoiceBar={false}
          myAvatarUrl={myAvatarUrl}
          displayName={displayName}
          voiceSession={null}
          activeVoiceName=""
          voiceVideoErr=""
          onToggleVoiceMute={null}
          onToggleVoiceDeafen={null}
          voiceShowVideo={false}
          onToggleVoiceVideo={null}
          isOwner={false}
          openServerSettings={() => {}}
          onOpenTab={onOpenTab}
          onLeaveVoiceChannel={null}
        />
      </div>

      {userMenu ? (
        <div
          style={contextMenuStyle(userMenu.x, userMenu.y)}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          role="menu"
          aria-label={`Actions for ${userMenu.label}`}
        >
          <button
            type="button"
            style={contextMenuButtonStyle(true)}
            onClick={handleToggleBlockFromMenu}
            disabled={mutatingUserId === toInt(userMenu.user_id, 0)}
          >
            <span>
              {mutatingUserId === toInt(userMenu.user_id, 0)
                ? userMenu.is_blocked
                  ? 'Unblocking…'
                  : 'Blocking…'
                : userMenu.is_blocked
                  ? 'Unblock user'
                  : 'Block user'}
            </span>
            <span style={{ opacity: 0.7 }}>
              {userMenu.is_blocked ? '↩' : '⛔'}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}